import { addOwnerName, checkOwnerName, strings, text } from "@quickpoint.me/shared";
import { createUserWithEmailAndPassword, getAuth } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import { useOwnerNameAvailability } from "./useOwnerNameAvailability";
import {
	useRecoilCallback,
	useRecoilState,
	useRecoilValue,
	useSetRecoilState,
} from "recoil";
import {
	clearOwnerNameFromSessionStorage,
	setOwnerNameToSessionStorage,
} from "../../../lib/ownerNameSessionStorage";
import {
	AuthScreenAtom,
	CreatingFirebaseAccount,
	FirebaseAccountCreated,
	FirebaseUser,
	IsCheckingOwnerName,
	NetworkError,
	OwnerNameExists,
} from "../../../state/authAtoms";
import { AuthPillButton, Banner, StyledError, StyledInfo } from "../styled/Components";

import { cloneDeep } from "lodash-es";
import { useForm } from "react-hook-form";
import Turnstile from "react-turnstile";
import { getTurnstileSiteKey } from "../../../lib/getTurnstileSiteKey";
import parseFirebaseErrors from "../../../lib/parseFirebaseErrors";
import { LoginSuccessfulResetState } from "../../../state/authSelectors";
import { IsInPortrait } from "../../../state/selectors";
import { DemoMode } from "../../../state/uiAtoms";
import { StyledForm } from "../LoginWithEmail";
import { StyledInput } from "../styled/StyledInput";
import { StyledWrapper } from "../styled/StyledWrapper";
import { TermsOfService } from "./SignUp";
import { uploadRecord } from "./uploadRecord";
import { track } from "../../../lib/metrics";
import { trackSignupConversion } from "../../../lib/conversionTracking";

type SignUpFormValues = {
	ownerName: string;
	email: string;
	password: string;
};

// use the supplied ownerName in register method in ownerName form field
function SignUpWithEmail({ ownerName }: { ownerName: string }) {
	const [creatingFirebaseAccount, setCreatingFirebaseAccount] = useRecoilState(
		CreatingFirebaseAccount,
	);
	const [firebaseAccountCreated, setFirebaseAccountCreated] = useRecoilState(
		FirebaseAccountCreated,
	);
	const [firebaseError, setFirebaseError] = useState(false);
	const [isCheckingOwnerName, setIsCheckingOwnerName] =
		useRecoilState(IsCheckingOwnerName);
	const [networkError, setNetworkError] = useRecoilState(NetworkError);
	const [ownerNameExists, setOwnerNameExists] = useRecoilState(OwnerNameExists);
	const setDemoMode = useSetRecoilState(DemoMode);
	const setFireBaseUser = useSetRecoilState(FirebaseUser);
	const setLoginSuccessfulResetState = useSetRecoilState(
		LoginSuccessfulResetState,
	);
	const {
		watch,
		register,
		formState: { errors },
		handleSubmit,
	} = useForm<SignUpFormValues>();
	const formRef = useRef<HTMLFormElement>(null);
	const isInPortrait = useRecoilValue(IsInPortrait);
	const watchedOwnerName = watch("ownerName");
	const [turnstileToken, setTurnstileToken] = useState<string>();
	const [disabled, setDisabled] = useState(true);
	const nameAvailability = useOwnerNameAvailability(watchedOwnerName ?? ownerName);

	// a stale "name taken" from an earlier submit clears as soon as the user edits
	useEffect(() => {
		setOwnerNameExists(false);
	}, [watchedOwnerName, setOwnerNameExists]);

	if (ownerName) ownerName = ownerName.toLowerCase();

	const submit = () => {
		if (formRef.current) {
			formRef.current.dispatchEvent(
				new Event("submit", { cancelable: true, bubbles: true }),
			);
		}
	};

	async function onSubmit({ ownerName, email, password }: SignUpFormValues) {
		if (!turnstileToken) return;
		ownerName = ownerName.toLowerCase();
		setIsCheckingOwnerName(true);
		// advisory availability check — /register below is the authority
		const checkName = await checkOwnerName(ownerName);

		if (checkName.networkError) {
			setNetworkError(true);
			setOwnerNameExists(false);
			setIsCheckingOwnerName(false);
			return;
		}

		if (!checkName.success) {
			setIsCheckingOwnerName(false);
			setCreatingFirebaseAccount(false);
			setOwnerNameExists(true);
			clearOwnerNameFromSessionStorage();
			return;
		}

		setOwnerNameExists(false);
		setIsCheckingOwnerName(false);
		setCreatingFirebaseAccount(true);

		// set this here. Firebase onAuth fires before the register call resolves,
		// so it has to be set before being sure the sign up process has completed.
		// It is cleared if there's an error
		setOwnerNameToSessionStorage(ownerName);

		// init the firebase sign up.
		const auth = getAuth();
		try {
			const credential = await createUserWithEmailAndPassword(
				auth,
				email,
				password,
			);

			const user = credential.user;
			// then claim the owner name atomically in the quickpoint system
			const jwt = await auth.currentUser?.getIdToken();
			if (!jwt) throw Error("SignUpWithEmail: Bad JWT");
			const addOwner = await addOwnerName({
				owner: ownerName,
				jwt,
				turnstileToken,
			});

			if (addOwner.success) {
				setCreatingFirebaseAccount(false);
				setFirebaseAccountCreated(true);
				setOwnerNameToSessionStorage(ownerName);
				uploadRecord(email);
				// Track signup event
				track(user.uid, 'signup', { provider: 'email', ownerName });
				// Send conversion to Meta & Reddit via CAPI
				trackSignupConversion(email);

				// give the user 3 seconds to digest the enormity of what has just occurred
				setTimeout(() => {
					const recoilUser = cloneDeep(user);
					setFireBaseUser(recoilUser);
					setLoginSuccessfulResetState(true);
					setDemoMode(true);
				}, 3000);
			} else {
				// name lost between check and register, or the account already has one
				setCreatingFirebaseAccount(false);
				setIsCheckingOwnerName(false);
				clearOwnerNameFromSessionStorage();
				if (addOwner.message === strings.workers.ownerExists) {
					setOwnerNameExists(true);
				} else {
					setNetworkError(true);
				}
			}
		} catch (error: unknown) {
			// @ts-expect-error: must type this
			if (error.code) {
				// @ts-expect-error
				setFirebaseError(parseFirebaseErrors(error));
				setOwnerNameExists(false);
				setCreatingFirebaseAccount(false);
				setIsCheckingOwnerName(false);
				clearOwnerNameFromSessionStorage();
			}
		}
	}

	const goBack = useRecoilCallback(({ set }) => () => {
		set(AuthScreenAtom, "signUp");
	});

	return (
		<StyledInput $isInPortrait={isInPortrait}>
			<Banner />
			<StyledInfo className="info pick-account-name">
				<p>{text.auth.pickNamePrompt}</p>
				<p>
					{text.auth.storiesUrlPrefix}
					<span className="pick-account-name-live">
						{watchedOwnerName ? watchedOwnerName : ownerName}
					</span>
				</p>
			</StyledInfo>
			<StyledForm>
				<div className="content">
					<form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
						<StyledWrapper>
							<label className="owner-name-label" htmlFor="ownerName">
								{text.auth.nameLabel}
							</label>
							<input
								aria-invalid={!!errors.ownerName}
								placeholder={text.auth.namePlaceholder}
								className="auth-input auth-owner-name-input input-box-as-line"
								id="ownerName"
								type="text"
								autoComplete="off"
								inputMode="text"
								autoCorrect="off"
								spellCheck={false}
								{...register("ownerName", {
									value: ownerName,
									required: true,
									maxLength: 100,
									minLength: 3,
									pattern: /^[a-zA-Z0-9_-]*$/i,
								})}
							/>
						</StyledWrapper>
						<StyledWrapper>
							<label className="owner-email-label" htmlFor="email">
								{text.auth.emailLabel}
							</label>
							<input
								aria-invalid={!!errors.email}
								placeholder={text.auth.signUpEmailPlaceholder}
								className="auth-input auth-owner-email-input input-box-as-line"
								autoComplete="email"
								onKeyDown={(e) => {
									if (e.key === "Enter") submit();
								}}
								id="email"
								type="email"
								{...register("email", {
									required: true,
									pattern: {
										value: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,10})+$/,
										message: "",
									},
								})}
							/>
						</StyledWrapper>
						<StyledWrapper>
							<label className="owner-password-label" htmlFor="password">
								{text.auth.passwordLabel}
							</label>
							<input
								aria-invalid={errors.password ? true : false}
								placeholder={text.auth.signUpPasswordPlaceholder}
								onKeyDown={(e) => {
									if (e.key === "Enter") submit();
								}}
								autoComplete="new-password"
								className="auth-input auth-owner-password-input input-box-as-line"
								id="password"
								type="password"
								{...register("password", {
									required: true,
									minLength: 8,
								})}
							/>
						</StyledWrapper>
						<StyledError role="alert">
							{errors.ownerName && errors.ownerName.type === "minLength" && (
								<span>{text.auth.nameTooShort}</span>
							)}
							{errors.ownerName && errors.ownerName.type === "maxLength" && (
								<span>{text.auth.nameTooLong}</span>
							)}
							{(ownerNameExists || nameAvailability === "taken") && (
								<span>{text.auth.nameTaken}</span>
							)}
							{errors.ownerName && errors.ownerName.type === "pattern" && (
								<span>{text.auth.nameInvalidChars}</span>
							)}
							{errors.password && errors.password.type === "minLength" && (
								<span>{text.auth.passwordTooShort}</span>
							)}
							{errors.email && <span>{text.auth.emailInvalid}</span>}
						</StyledError>
						<StyledError>
							{firebaseError && <span>{firebaseError}</span>}
							{networkError && (
								<span>{text.auth.signUpError}</span>
							)}
							{isCheckingOwnerName && (
								<span>{text.auth.checkingName}</span>
							)}
							{creatingFirebaseAccount && (
								<span>{text.auth.nameAvailable}</span>
							)}
							{firebaseAccountCreated && <span>{text.auth.accountReady}</span>}
						</StyledError>
					</form>
				</div>
			</StyledForm>
			<div className="buttons">
				<Turnstile
					style={{
						transition: disabled ? "none" : "opacity 1s ease-out",
						opacity: disabled ? 1 : 0.3,
					}}
					sitekey={getTurnstileSiteKey()}
					theme="light"
					fixedSize={true}
					retry="auto"
					refreshExpired="auto"
					onVerify={(token) => {
						setTurnstileToken(token);
						setDisabled(false);
					}}
					onError={() => {
						// transient challenge failures (common under Safari ITP) —
						// drop the token and let retry="auto" re-run the challenge
						setTurnstileToken(undefined);
						setDisabled(true);
					}}
					onExpire={() => {
						setTurnstileToken(undefined);
						setDisabled(true);
					}}
				/>
				<AuthPillButton
					id="auth-owner-name-button"
					disabled={disabled || nameAvailability === "taken"}
					onClick={submit}
				>
					{text.auth.register}
				</AuthPillButton>
				<AuthPillButton $variant="primaryOutline" onClick={() => goBack()}>
					{text.auth.back}
				</AuthPillButton>
				<TermsOfService />
			</div>
		</StyledInput>
	);
}

export default SignUpWithEmail;
